<template>
  <div ref="textHeroRef" class="app-text-hero flex flex-col gap-6 p-6">
    <div ref="titleRef" class="app-text-hero__content" v-html="title" />

    <div ref="actionsRef" class="app-text-hero__actions">
      <slot name="actions" />
    </div>

    <div ref="descriptionRef" class="app-text-hero__description font-ds-large-body-lg" v-html="description" />

    <div ref="actionsFooterRef" class="app-text-hero__actions-footer">
      <slot name="actions-footer" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  const { $gsap, $SplitText } = useNuxtApp();

  const titleRef = useTemplateRef('titleRef');
  const textHeroRef = useTemplateRef('textHeroRef');
  const actionsRef = useTemplateRef('actionsRef');
  const descriptionRef = useTemplateRef('descriptionRef');
  const actionsFooterRef = useTemplateRef('actionsFooterRef');

  defineProps<{
    title: string;
    description: string;
  }>();

  function animate() {
    const headingElements = titleRef.value?.querySelectorAll('h1, h2');

    const timeline = $gsap.timeline({
      scrollTrigger: {
        trigger: textHeroRef.value,
        start: 'top 80%',
        toggleActions: 'play none none none',
      },
    });

    $SplitText.create(titleRef.value, {
      type: 'words',
      autoSplit: true,
      mask: 'words',
      onSplit(self) {
        timeline.set(titleRef.value, {
          autoAlpha: 1,
        });

        timeline.from(self.words, {
          duration: 1,
          y: 50,
          autoAlpha: 0,
          stagger: 0.02,
          ease: 'power2.out',
        });

        if (headingElements?.length) {
          timeline.to(
            headingElements,
            {
              duration: 1,
              '--heading-icon-opacity': 1,
              '--heading-icon-y': '0px',
              ease: 'power2.out',
            },
            '<',
          );
        }

        timeline.to(
          actionsRef.value,
          {
            duration: 1,
            autoAlpha: 1,
            ease: 'power2.out',
          },
          '<=0.3',
        );
      },
    });

    $SplitText.create(descriptionRef.value, {
      type: 'words',
      autoSplit: true,
      mask: 'words',
      onSplit(self) {
        timeline.set(
          descriptionRef.value,
          {
            autoAlpha: 1,
          },
          '<0.2',
        );

        timeline.from(
          self.words,
          {
            duration: 1,
            y: 50,
            autoAlpha: 0,
            stagger: 0.02,
            ease: 'power2.out',
          },
          '<0.2',
        );

        timeline.to(
          actionsFooterRef.value,
          {
            duration: 1,
            autoAlpha: 1,
            ease: 'power2.out',
          },
          '<=0.8',
        );
      },
    });
  }

  onMounted(() => {
    animate();
  });
</script>

<style lang="postcss" scoped>
  .app-text-hero {
    :deep(b) {
      font-weight: 600;
    }

    :deep(h2) {
      --heading-icon-opacity: 0;
      --heading-icon-y: 50px;

      &::before {
        content: '';

        transform: translateY(var(--heading-icon-y));

        display: inline-block;

        width: 0.45em;
        height: 1em;
        margin-inline-end: var(--spacing-2);

        vertical-align: -0.08em;

        opacity: var(--heading-icon-opacity);
        background-color: currentcolor;

        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 30'%3E%3Cpath d='M11.2637 0L0 30H2.73628L14 0H11.2637Z' fill='currentColor'/%3E%3C/svg%3E");
        mask-repeat: no-repeat;
        mask-position: center;
        mask-size: contain;
      }
    }

    &__actions,
    &__content,
    &__description,
    &__actions-footer {
      visibility: hidden;
      opacity: 0;

      &:empty {
        display: none;
        margin-top: calc(var(--spacing-6) * -1);
      }
    }
  }
</style>
